import React from 'react';
import { Phone, MapPin, Mail, ArrowLeft, Briefcase, DollarSign, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

const Careers = () => {
  const navigate = useNavigate();

  const jobOpenings = [
    {
      id: 1,
      title: 'Project Manager / Estimator',
      type: 'Full-Time',
      location: 'De Pere, WI',
      salary: 'Competitive',
      description: 'Turriff Plumbing & Heating is seeking a Project Manager / Estimator to help manage commercial plumbing projects from initial bid through closeout. This position combines estimating and project management and will serve as the primary office-side owner of assigned commercial projects. The PM/Estimator will prepare estimates, lead project buyouts, coordinate preconstruction activities, monitor active projects, and help ensure work is properly prepared, documented, billed, and financially managed. The position will work closely with the General Manager, Office Coordinator, ownership, suppliers, general contractors, and field employees. The PM/Estimator is responsible for helping ensure projects are ready so the General Manager can effectively schedule and execute the work.',
      whatYoullDo: null,
      lookingFor: 'We are looking for someone who takes ownership, anticipates problems, communicates early, and understands that successful project management requires both financial discipline and strong support of the field team. The right person will be comfortable moving between estimating, purchasing, project coordination, customer communication, and financial review while helping Turriff Plumbing build scalable processes as the company grows.',
      responsibilityGroups: [
        {
          heading: 'Estimating & Preconstruction',
          items: [
            'Review RFPs, plans, specifications, addenda, and project requirements',
            'Perform plumbing takeoffs and prepare labor, material, equipment, and subcontractor estimates',
            'Develop proposals, clarifications, exclusions, alternates, and RFIs as needed',
            'Participate in bid reviews and project walkthroughs',
            'Following award, transition projects from estimate into active preconstruction',
            'Coordinate design questions, state plan review, permitting, submittals, and other preconstruction requirements with the General Manager and Office Coordinator',
            'Identify long-lead materials, fixtures, equipment, and project risks before work begins'
          ]
        },
        {
          heading: 'Purchasing & Buyout',
          items: [
            'Obtain competitive buy pricing for fixtures, equipment, and major material packages',
            'Compare pricing, lead times, availability, substitutions, freight, and supplier terms',
            'Negotiate pricing and determine which supplier should receive the order',
            'Communicate approved purchasing decisions to the Office Coordinator',
            'Monitor significant material costs and long-lead items'
          ]
        },
        {
          heading: 'Project Management',
          items: [
            'Serve as a primary office-side contact for assigned projects',
            'Track RFIs, submittals, change orders, materials, project documentation, and open action items',
            'Maintain regular communication with general contractors, suppliers, the General Manager, and field employees',
            'Help ensure projects are ready for upcoming demo, underground, rough-in, and finish work',
            'Coordinate material releases and deliveries based on field needs and project schedule',
            'Identify scope changes and prepare change-order pricing',
            'Coordinate project invoicing milestones with the Office Coordinator',
            'Coordinate closeout requirements, including punch lists, warranty letters, O&M manuals, and as-built drawings'
          ]
        },
        {
          heading: 'Scheduling & Project Readiness',
          items: [
            'Participate in the rolling project schedule review with the General Manager',
            'Help maintain a reasonably reliable three-week project look ahead',
            'Confirm that materials, approvals, inspections, access, information, and other requirements are in place before work is scheduled',
            'Identify project or schedule risks early'
          ]
        },
        {
          heading: 'Project Financial Performance',
          items: [
            'Monitor labor and material performance against the original estimate',
            'Maintain visibility into project budget, committed costs, changes, and expected profitability',
            'Identify potential overruns or unfavorable trends before project completion',
            'Participate in project close-out and estimate-to-actual reviews',
            'Use completed project results to improve future estimating and project execution'
          ]
        }
      ],
      requirements: [
        'Experience in commercial plumbing, mechanical contracting, construction estimating, or project management',
        'Ability to read and interpret construction plans and specifications',
        'Experience with construction estimating, material takeoffs, and project coordination',
        'Understanding of plumbing materials, fixtures, equipment, and construction sequencing preferred',
        'Strong organizational, communication, and follow-through skills',
        'Comfortable managing multiple active bids and projects simultaneously',
        'Proficient with Excel, Outlook, and construction management or estimating software',
        'Plumbing field experience is highly desirable, but a plumbing license is not required for the right candidate'
      ]
    },
    {
      id: 2,
      title: 'Service Lead to Service Manager – Commercial and Residential',
      type: 'Full-Time',
      location: 'De Pere, WI',
      salary: 'Competitive',
      description: 'Turriff Plumbing is a fifth-generation, family-owned plumbing contractor proudly serving Wisconsin since 1920. Built on over a century of craftsmanship, trust, and community commitment, we provide a full range of plumbing services to both commercial and residential clients across Northeast Wisconsin. From large-scale new construction to service repairs and preventative maintenance, we\'ve built a reputation for doing the job right—and treating people right. We\'re a close-knit, hardworking team that values safety, professionalism, and mentorship.',
      whatYoullDo: 'The Services Lead/Manager is responsible for leading, coordinating, and improving Turriff Plumbing\'s service operations. Initially this individual will be responsible for both completing service calls and building his or her team. This will be a working manager position that will transition to a more traditional manager role as the team grows and achieves defined performance targets, such as meeting sales goals or completing key projects. This role will ultimately bridge the gap between the field and the office by providing leadership to service technicians, coordinating schedules and resources, supporting customer communication, and driving operational efficiency, safety, and quality standards. This role is ideal for an ambitious individual who is looking to build and lead a new dedicated division for a 105+ year old plumbing business.',
      responsibilities: [
        'Oversee day-to-day service operations, including service calls, maintenance work, and small projects. Initially perform majority of service calls',
        'Hire, lead, mentor, and support service technicians; provide coaching, training, and performance feedback',
        'Coordinate scheduling, dispatch, and workload distribution in partnership with office staff',
        'Serve as an escalation point for complex troubleshooting, customer concerns, and technical questions',
        'Ensure service work meets company quality standards, safety requirements, and applicable codes',
        'Collaborate with project managers, estimators, and leadership to align service operations with company goals',
        'Review service reports, work orders, and documentation for accuracy and completeness',
        'Support hiring, onboarding, and training of service personnel',
        'Promote a strong safety culture; enforce OSHA, company, and state safety procedures',
        'Identify opportunities for process improvement, efficiency gains, and customer satisfaction'
      ],
      requirements: [
        'Journeyman or Master Plumber license (Wisconsin)',
        '6+ years of plumbing experience with a strong background in service work',
        'Prior leadership, supervisory, or lead technician experience preferred',
        'Strong problem-solving, communication, and organizational skills',
        'Ability to manage multiple priorities and grow into a leadership role',
        'Proficiency with smartphones, tablets, and service/dispatch software',
        'Valid driver\'s license and insurable driving record'
      ]
    },
    {
      id: 3,
      title: 'Licensed Journeyman Plumber',
      type: 'Full-Time, Hourly – Non-Exempt',
      location: 'De Pere, WI (service radius ≈ 60 miles across Northeast Wisconsin)',
      salary: 'Hourly',
      description: 'Turriff Plumbing is a fifth-generation, family-owned plumbing contractor proudly serving Wisconsin since 1920. Built on over a century of craftsmanship, trust, and community commitment, we provide a full range of plumbing services to both commercial and residential clients across Northeast Wisconsin. From large-scale new construction to service repairs and preventative maintenance, we\'ve built a reputation for doing the job right—and treating people right. We\'re a close-knit, hardworking team that values safety, professionalism, and mentorship. Whether you\'re working on a new build, a tenant improvement, or a service call, you\'ll be backed by leadership that understands life in the field—because we\'ve been there for generations.',
      whatYoullDo: null,
      responsibilities: [
        'Work across job types: new builds, remodels/building repurpose, and service calls',
        'Install, maintain and repair water, waste, gas and hydronic systems',
        'Interpret blueprints, specifications, and job packets; lay out plumbing systems, as well as inert gas systems',
        'Mark underground piping locations; prepare layouts for trenching or slab cuts; coordinate saw cuts/core drilling',
        'Diagnose and troubleshooting is a plus; communicate clear repair options to customers and dispatch',
        'Work collaboratively with team members and supervisors',
        'Coordinate with project managers, GCs, and office staff to meet deadlines and budgets',
        'Maintain a clean, safe job site; follow OSHA, company and state safety procedures',
        'Operate heavy machinery including excavators, skid steers, sky tracks, dump trucks, and various manlifts'
      ],
      requirements: [
        'Current Wisconsin Journeyman Plumber license (SPS 305.93 compliant)',
        'Valid driver\'s license and insurable driving record',
        '4+ years of plumbing experience (commercial or mixed)',
        'Ability to lift 65lbs, climb ladders, and work in confined spaces',
        'Basic proficiency with smartphones/tablets for time tracking and work orders'
      ],
      schedule: 'Core hours: Monday–Friday, 7:00 AM–3:30 PM (paid portal-to-portal). Overtime typically available during peak build season; voluntary Saturdays only.',
      benefits: '401(k) with 3% company match after waiting period. Health (100% paid), dental & vision insurance after waiting period.'
    }
  ];

  const benefits = [
    { icon: DollarSign, title: 'Competitive Pay', description: 'Industry-leading wages' },
    { icon: Briefcase, title: '401(k) Match', description: '3% company match' },
    { icon: Users, title: 'Health Benefits', description: 'Health, dental & vision insurance' },
    { icon: Clock, title: 'Career Growth', description: 'Advancement to Master Plumber, Supervisor, or Estimator' }
  ];

  const handleApply = (jobTitle) => {
    const mailtoLink = `mailto:jobs@turriffplumbing.com?subject=${encodeURIComponent('Application for ' + jobTitle)}`;
    window.open(mailtoLink, '_self');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src="/images/turriff-logo-wide-color.png" 
                alt="Turriff Plumbing - Since 1920" 
                className="h-12 md:h-14 w-auto object-contain"
              />
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
                className="hidden md:flex"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
              <Button 
                onClick={() => window.location.href = 'tel:920-336-9455'}
                className="bg-primary-blue hover:bg-primary-blue-dark text-white px-3 py-2 md:px-6 md:py-3 text-sm md:text-base"
              >
                <Phone className="mr-1 md:mr-2 h-4 w-4 md:h-5 md:w-5" />
                <span className="hidden sm:inline">920-336-9455</span>
                <span className="sm:hidden">Call</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">Join Our Team</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Be part of a family-owned business with over 100 years of excellence. 
            We're looking for skilled professionals to grow with us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center text-gray-700">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary-blue" />
              <span>1110 Honey Ct, De Pere, WI</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary-blue" />
              <span>jobs@turriffplumbing.com</span>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Work With Us?</h3>
          <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <Icon className="h-8 w-8 text-primary-blue" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h4>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Job Openings Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h3 className="text-4xl font-bold text-center text-gray-900 mb-4">Current Openings</h3>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Explore our available positions and take the next step in your plumbing career
          </p>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {jobOpenings.map((job) => (
              <Card key={job.id} className="hover:shadow-xl transition-shadow">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-white">
                  <div>
                    <CardTitle className="text-2xl text-gray-900 mb-2">{job.title}</CardTitle>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-4 w-4" />
                        {job.type}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4" />
                        {job.salary}
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-gray-700 mb-6 text-justify">{job.description}</p>
                  
                  {job.whatYoullDo && (
                    <div className="mb-6">
                      <h5 className="font-bold text-gray-900 mb-3">What You'll Do:</h5>
                      <p className="text-gray-700 text-justify">{job.whatYoullDo}</p>
                    </div>
                  )}
                  
                  {job.lookingFor && (
                    <div className="mb-6">
                      <h5 className="font-bold text-gray-900 mb-3">What We're Looking For:</h5>
                      <p className="text-gray-700 text-justify">{job.lookingFor}</p>
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <h5 className="font-bold text-gray-900 mb-3">{job.whatYoullDo ? 'Responsibilities:' : 'What You\'ll Do:'}</h5>
                    {job.responsibilityGroups ? (
                      <div className="space-y-4">
                        {job.responsibilityGroups.map((group, groupIndex) => (
                          <div key={groupIndex}>
                            <h6 className="font-semibold text-gray-800 mb-2">{group.heading}</h6>
                            <ul className="space-y-2">
                              {group.items.map((item, index) => (
                                <li key={index} className="flex items-start gap-2 text-gray-700">
                                  <span className="text-primary-blue mt-1">•</span>
                                  <span className="text-justify">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {job.responsibilities.map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-gray-700">
                            <span className="text-primary-blue mt-1">•</span>
                            <span className="text-justify">{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  
                  {job.schedule && (
                    <div className="mb-6">
                      <h5 className="font-bold text-gray-900 mb-3">Schedule & Work Environment:</h5>
                      <p className="text-gray-700 text-justify">{job.schedule}</p>
                    </div>
                  )}
                  
                  {job.benefits && (
                    <div className="mb-6">
                      <h5 className="font-bold text-gray-900 mb-3">Benefits:</h5>
                      <p className="text-gray-700 text-justify">{job.benefits}</p>
                    </div>
                  )}
                  
                  <div className="mb-6">
                    <h5 className="font-bold text-gray-900 mb-3">Required Qualifications:</h5>
                    <ul className="space-y-2">
                      {job.requirements.map((item, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-700">
                          <span className="text-primary-blue mt-1">•</span>
                          <span className="text-justify">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-gray-700 text-center">
                      <span className="font-semibold">To apply:</span> Email your resume to{' '}
                      <a 
                        href="mailto:jobs@turriffplumbing.com" 
                        className="text-primary-blue hover:underline font-semibold"
                      >
                        jobs@turriffplumbing.com
                      </a>
                      {' '}or call 920-336-9455
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-primary-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-4xl font-bold mb-4">Don't See the Right Position?</h3>
          <p className="text-xl mb-8 text-blue-100">
            We're always looking for talented individuals. Send us your resume!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => {
                const mailtoLink = 'mailto:jobs@turriffplumbing.com?subject=' + encodeURIComponent('General Application');
                window.open(mailtoLink, '_self');
              }}
              size="lg"
              variant="secondary"
              className="bg-white text-primary-blue hover:bg-gray-100"
            >
              <Mail className="mr-2 h-5 w-5" />
              Email Your Resume
            </Button>
            <Button 
              onClick={() => window.open('tel:920-336-9455', '_self')}
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-primary-blue"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call Us: 920-336-9455
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/images/turriff-logo.jpg" 
                  alt="Turriff Plumbing" 
                  className="h-12 w-12 object-contain"
                />
                <div>
                  <h4 className="text-xl font-bold">Turriff Plumbing</h4>
                  <p className="text-sm text-gray-400">Since 1920</p>
                </div>
              </div>
              <p className="text-gray-400">
                Over 100 years of trusted plumbing services in De Pere and surrounding areas.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <button onClick={() => navigate('/')} className="hover:text-white transition-colors">
                    Home
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/careers')} className="hover:text-white transition-colors">
                    Careers
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-4">Contact</h4>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-start gap-2">
                  <Phone className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <span>920-336-9455</span>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <span>jobs@turriffplumbing.com</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <span>1110 Honey Ct<br />De Pere, WI</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; {new Date().getFullYear()} Turriff Plumbing. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Careers;
